import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// ── Low-level helpers (used by existing pages) ──────────────────────────────

/**
 * Parse a comma-separated URL search param into a string array.
 */
export function parseArrayParam(params: URLSearchParams, key: string): string[] {
  const val = params.get(key);
  return val ? val.split(',').filter(Boolean) : [];
}

/**
 * Parse a single URL search param with a default fallback.
 */
export function parseStringParam(params: URLSearchParams, key: string, fallback: string): string {
  return params.get(key) || fallback;
}

// ── High-level hook for future pages ────────────────────────────────────────

type FilterType = 'string' | 'array' | 'boolean';

interface FilterSpec {
  /** URL param name */
  param: string;
  /** Type of filter value */
  type: FilterType;
  /** Default value — omitted from URL when active */
  default: string | string[] | boolean;
}

type FilterValue<T extends FilterType> =
  T extends 'string' ? string :
  T extends 'array' ? string[] :
  T extends 'boolean' ? boolean :
  never;

type FilterState<S extends Record<string, FilterSpec>> = {
  [K in keyof S]: FilterValue<S[K]['type']>;
};

type FilterSetters<S extends Record<string, FilterSpec>> = {
  [K in keyof S as `set${Capitalize<string & K>}`]: (value: FilterValue<S[K]['type']>) => void;
};

/**
 * Declarative URL-persisted filters. Define your filters once, get state + setters + URL sync.
 *
 * @example
 * const { filters, setters } = useUrlFilters({
 *   search:   { param: 'search',  type: 'string',  default: '' },
 *   status:   { param: 'status',  type: 'string',  default: 'all' },
 *   roles:    { param: 'role',    type: 'array',   default: [] },
 *   active:   { param: 'active',  type: 'boolean', default: false },
 * });
 *
 * // filters.search, filters.status, filters.roles, filters.active
 * // setters.setSearch('query'), setters.setStatus('won'), setters.setRoles(['admin'])
 */
export function useUrlFilters<S extends Record<string, FilterSpec>>(specs: S) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitRef = useRef(true);

  // Parse initial values from URL on mount
  const initialValues = useMemo(() => {
    const result: Record<string, any> = {};
    for (const [key, spec] of Object.entries(specs)) {
      const raw = searchParams.get(spec.param);
      if (spec.type === 'array') {
        result[key] = raw ? raw.split(',').filter(Boolean) : (spec.default as string[]);
      } else if (spec.type === 'boolean') {
        result[key] = raw !== null ? raw === '1' : (spec.default as boolean);
      } else {
        result[key] = raw || (spec.default as string);
      }
    }
    return result;
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [state, setState] = useState<Record<string, any>>(initialValues);

  // Build typed setters: setSearch, setStatus, setRoles, etc.
  const setters = useMemo(() => {
    const result: Record<string, (value: any) => void> = {};
    for (const key of Object.keys(specs)) {
      const setterName = `set${key.charAt(0).toUpperCase()}${key.slice(1)}`;
      result[setterName] = (value: any) => {
        setState((prev) => ({ ...prev, [key]: value }));
      };
    }
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync state → URL search params
  useEffect(() => {
    // Skip the initial render to avoid overwriting params on mount
    if (isInitRef.current) {
      isInitRef.current = false;
      return;
    }

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      for (const [key, spec] of Object.entries(specs)) {
        const value = state[key];
        const isDefault =
          spec.type === 'array'
            ? (value as string[]).length === 0
            : spec.type === 'boolean'
              ? value === spec.default
              : value === spec.default || value === '';

        if (isDefault) {
          params.delete(spec.param);
        } else if (spec.type === 'array') {
          params.set(spec.param, (value as string[]).join(','));
        } else if (spec.type === 'boolean') {
          params.set(spec.param, value ? '1' : '0');
        } else {
          params.set(spec.param, value as string);
        }
      }
      return params;
    }, { replace: true });
  }, [state, specs, setSearchParams]);

  return {
    filters: state as FilterState<S>,
    setters: setters as FilterSetters<S>,
    setFilter: <K extends keyof S>(key: K, value: FilterValue<S[K]['type']>) => {
      setState((prev) => ({ ...prev, [key]: value }));
    },
    resetFilters: () => setState({ ...initialValues }),
  };
}
